import { cn } from "../../lib/utils"

const MODIFIER = /[⌘⌥⌃⇧]/

/**
 * Split a compact shortcut string into its individual keys. Explicit
 * separators (`+` or whitespace) win; otherwise each leading modifier glyph
 * becomes its own key and the remainder is a single key, so "⌘J" yields
 * ["⌘", "J"] and "⌘⇧K" yields ["⌘", "⇧", "K"].
 */
export const parseShortcut = (shortcut: string): string[] => {
  if (/[+\s]/.test(shortcut)) {
    return shortcut.split(/[+\s]+/).filter(Boolean)
  }
  const keys: string[] = []
  let rest = shortcut
  while (rest.length > 0 && MODIFIER.test(rest.charAt(0))) {
    keys.push(rest.charAt(0))
    rest = rest.slice(1)
  }
  if (rest) keys.push(rest)
  return keys
}

export interface KbdProps {
  /**
   * The shortcut. A string is split into distinct keys ("⌘J" renders as two
   * chips); pass an array to control the split yourself.
   */
  keys: string | string[]
  /** Visual tone: `default` for light surfaces, `inverted` on a dark surface. */
  tone?: "default" | "inverted"
  className?: string
}

/** Renders a keyboard shortcut as one distinct `<kbd>` chip per key. */
export const Kbd = ({ keys, tone = "default", className }: KbdProps) => {
  const parts = Array.isArray(keys) ? keys : parseShortcut(keys)
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      {parts.map((token, index) => (
        <kbd
          key={`${token}-${index}`}
          className={cn(
            "inline-flex min-w-[1.1rem] items-center justify-center rounded px-1 py-0.5 font-mono text-[10px] leading-none",
            tone === "inverted"
              ? "bg-background/20 text-background"
              : "border border-border bg-muted text-muted-foreground shadow-sm",
          )}
        >
          {token}
        </kbd>
      ))}
    </span>
  )
}
